import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SectionHead from '../../components/common/SectionHead';
import Card from '../../components/common/Card';
import Field from '../../components/common/Field';
import EmptyState from '../../components/common/EmptyState';
import Spinner from '../../components/common/Spinner';
import LinkForm from '../links/LinkForm';
import linksService from '../../api/services/links.service';
import roomParentsService from '../../api/services/roomParents.service';
import teacherEmailsService from '../../api/services/teacherEmails.service';
import sentEmailsService from '../../api/services/sentEmails.service';
import emailTemplatesService from '../../api/services/emailTemplates.service';
import signupsService from '../../api/services/signups.service';
import useEventsStore from '../../store/useEventsStore';
import useGmailStore from '../../store/useGmailStore';
import { sendGmailMessage, fileToBase64 } from '../../api/gmail';
import useEmailStore from '../../store/useEmailStore';
import useSignupsStore, { signupLinkUrl } from '../../store/useSignupsStore';
import useContextStore, { selectCurrentContext } from '../../store/useContextStore';
import useMyProfileStore, { selectMyMetadata } from '../../store/useMyProfileStore';
import { showToast } from '../../store/useToastStore';
import { fmtDate, fmtEventTime, fmtDateTime } from '../../utils/formatters';
import { consentFormLinkUrl, groupChatLinkUrl } from '../../utils/portalLinks';
import { isValidEmail, parseEmailList, uniqueEmails } from '../../utils/email';
import useClickOutside from '../../hooks/useClickOutside';
import useMediaQuery from '../../hooks/useMediaQuery';
import ComposeBlock from './ComposeBlock';
import PreviewSheet from './PreviewSheet';
import { IconChevron, IconCal, IconEdit, IconFile, IconLink } from '../../components/icons';
import RichEditor from '../../components/common/RichEditor';
import EmailAttachments, { AttachmentChips, IconPaperclip } from './EmailAttachments';
import useEmailAttachments from './useEmailAttachments';
import { templateBodyToHtml, decorateHtml, emailBodyStyles, htmlToText } from '../../utils/richText';
import { attachIssue } from '../../utils/attachments';

/* the one-line "what's in here" under a collapsed block's title */
const summarise = (names, max = 44) => {
  if (!names.length) return 'None selected';
  const joined = names.join(' · ');
  return joined.length > max ? `${joined.slice(0, max - 1)}…` : joined;
};

const escapeHtml = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
/* href/attribute context needs the quotes escaped too */
const escapeAttr = (s) => escapeHtml(s).replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/* Class links are typed by hand, so normalize them the way Copy link does — a
   bare domain gets https://, and anything with some other scheme (javascript:,
   data:) is refused rather than turned into a live link in the preview. */
const safeUrl = (raw) => {
  const u = String(raw || '').trim();
  if (!u) return '';
  if (/^(https?:|mailto:)/i.test(u)) return u;
  if (/^[a-z][a-z0-9+.-]*:/i.test(u)) return '';
  return `https://${u}`;
};
/* An unfilled spot in a template draft: a {{key}} the form hasn't answered yet,
   or a [bracket] left in hand-written copy. Both show red (the editor paints
   them) and block Send. */
const PH_RE = /(\{\{[^}\n]+\}\}|\[[^\]\n]+\])/;

/* Recipient modes that put the teacher on the email — all three edit the same
   saved list of teacher addresses. */
const TEACHER_MODES = ['all_teacher', 'emailonly_teacher', 'teacher'];

const SENT_PAGE_SIZE = 5;

/* ── ROOM PARENT INTRO TEMPLATE ──
   The client's approved copy, expressed the way the templates API stores one:
   body text with {{key}} placeholders plus the definition of the form that
   fills them. It isn't hard-wired into the page any more — it's what "Add the
   Room Parent Intro template" saves, after which it arrives from the API like
   any other template, and new templates need no code here at all. */
const ROOM_PARENT_INTRO_TEMPLATE = {
  name: 'Room Parent Intro',
  description: 'Introduce yourselves — goes to the teacher first',
  subject: 'Room Parent Email',
  fields: [
    { key: 'rp1Name', label: 'Room Parent 1 — Name', type: 'member', placeholder: 'e.g. Tony Martin', isRequired: true },
    { key: 'rp1Email', label: 'Room Parent 1 — Email', type: 'email', placeholder: 'tony.martin@gmail.com', isRequired: true },
    { key: 'rp2Name', label: 'Room Parent 2 — Name', type: 'member', placeholder: 'e.g. Kate Skiff', isRequired: true },
    { key: 'rp2Email', label: 'Room Parent 2 — Email', type: 'email', placeholder: 'kate.skiff@outlook.com', isRequired: true },
    { key: 'teacherName', label: "Teacher's Name", type: 'text', placeholder: 'Mrs. Taylor', isRequired: true },
    {
      key: 'teacherEmail',
      label: "Teacher's Email",
      type: 'email',
      placeholder: 'mstaylorclass@kikerelementary.edu',
      helpText: 'This is who the email goes to — the teacher forwards it on.',
      isRequired: true,
    },
    {
      key: 'deadline',
      label: 'Form Deadline',
      type: 'date',
      helpText: "Shows in bold in the email — after this date you'll assume no permission to contact.",
      isRequired: true,
    },
  ],
  body: `Hello Parents,

We hope your school year is off to a great start! We wanted to introduce ourselves as {{teacherName}} room parents for the year, {{rp1Name}}  and {{rp2Name}}

Together we will be working to support our wonderful teacher by planning and collecting funds for AISD Appreciation Days, class activities, gifts and—of course—being a point of contact for you should you need it. Thank you in advance for your time and contributions!

We look forward to meeting all of you. We will be meeting with {{teacherName}} soon to make a plan for the semester regarding ways we can all help the class. We will then send out a more detailed email with important dates for our class, as well as links to donate and ways to get involved—all of which are completely optional, but highly appreciated.

For now, please fill out the attached form HERE so that we may gather your contact information as well as have permission to contact you directly if you so choose. This form is optional. If you do not fill this form out by {{deadline}}, we will assume you do not give us permission to contact you and you will only be included on class emails directly from {{teacherName}}.

There is also a question at the end about joining a Class Group Chat.  We would like to gauge everyone's interest in before creating. This would be used to coordinate birthday plans, play dates and any random questions you may have throughout the year. Please don't hesitate to reach out to us by email (see below) if you have any questions!

Thank you so much, everyone! We wish you all a lovely four day weekend :)

— The {{teacherName}} Class Room Parents

{{rp1Name}}: {{rp1Email}}
{{rp2Name}}: {{rp2Email}}`,
};

/* Picker for a template's `member` field — a custom rich-select (button + our
   own positioned menu), not a native <select>, so the option list can be styled
   and positioned by the page. */
function MemberPicker({ label, options, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, useCallback(() => setOpen(false), []));
  return (
    <div className="rich-select" ref={ref}>
      <button type="button" className="rich-select-btn" onClick={() => setOpen((o) => !o)}>
        <span>{label}</span>
        <IconChevron />
      </button>
      {open && (
        <div className="rich-select-menu open">
          {options.map((p) => (
            <div className="rich-select-item" key={p.id} onClick={() => { onPick(p); setOpen(false); }}>
              <span>
                {p.name}
                {p.email ? ' · ' + p.email : ''}
              </span>
            </div>
          ))}
          <div className="rich-select-item" onClick={() => { onPick(null); setOpen(false); }}>
            <span>Someone else — type it below</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ComposeEmailPage() {
  const ctx = useContextStore(selectCurrentContext);
  const { events, eventsLoading, loadEvents, setIncludeInEmail, markEventsSent } = useEventsStore();
  const gmailAccount = useGmailStore((s) => s.account);
  const { sentEmails, sentEmailsLoading, sentEmailsTotal, sentEmailsTotalPages, loadSentEmails, prependSentEmail } =
    useEmailStore();
  const { signups, setIncludeInEmail: setSignupInclude, loadSignups, patchSignup } = useSignupsStore();
  const meta = useMyProfileStore(selectMyMetadata);
  const location = useLocation();

  const myFirstName = (meta?.name || '').trim().split(/\s+/)[0];
  const networkId = ctx?.id || null;
  /* the two links every class has, same source as the Links page */
  const consentUrl = consentFormLinkUrl(networkId);
  const chatUrl = groupChatLinkUrl(networkId, ctx?.publicURL);

  /* both start empty, showing their placeholders — an empty subject still
     sends as "Class Update" */
  const [subject, setSubject] = useState('');
  /* the message as the editor last reported it: sanitized HTML (p br b i u s a
     ul ol li only) and its plain-text twin, which the send gate and the dock
     read. The editor itself is driven through editorRef. */
  const [draft, setDraft] = useState({ html: '', text: '', empty: true });
  const editorRef = useRef(null);
  /* files ride with the draft: uploaded as they are picked, sent by reference
     on the record and as bytes through Gmail */
  const att = useEmailAttachments(networkId);
  const [recipientMode, setRecipientMode] = useState('all_teacher');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [teacherEmailInput, setTeacherEmailInput] = useState('');
  /* a fresh email carries the group chat, not the consent form; a "Send to
     parents" handoff or a template that links HERE still sets these itself */
  const [builtin, setBuiltin] = useState({ consent: false, groupchat: true });

  /* ── templates (API) ──
     Quick Actions lists what this class has; picking one loads its detail and
     the form below is built from that template's own field definitions. Nothing
     is applied until one is picked. */
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateBusyId, setTemplateBusyId] = useState(null);
  const [seedingTemplate, setSeedingTemplate] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [tplValues, setTplValues] = useState({});
  const [tplSendAttempted, setTplSendAttempted] = useState(false);
  /* label shown on a `member` field's picker button, per field key */
  const [memberLabels, setMemberLabels] = useState({});
  const tplCardRef = useRef(null);

  const templateActive = !!activeTemplate;

  /* Recipients + the Room Parent picker's options: the class's whole parent
     roster, the same list the Parents page shows. It includes roster-only
     contacts who never joined the app — they can only be reached by email, so
     leaving them out would quietly drop families from a class send. */
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  useEffect(() => {
    if (!networkId) {
      setMembers([]);
      return undefined;
    }
    let cancelled = false;
    setMembersLoading(true);
    roomParentsService
      /* this list is the actual recipient set, so pull the whole class rather
         than a first page of it */
      .getParents(networkId, { page: 1, limit: 200 })
      .then((r) => { if (!cancelled) setMembers(r.parents); })
      .catch((err) => {
        if (!cancelled) {
          setMembers([]);
          showToast(err.message);
        }
      })
      .finally(() => { if (!cancelled) setMembersLoading(false); });
    return () => { cancelled = true; };
  }, [networkId]);

  /* Compose Email | Previous Emails. The dashboard's "Last email sent" tile
     lands on Previous Emails; every other way in lands on Compose. */
  const [pane, setPane] = useState(location.state?.pane === 'prev' ? 'prev' : 'compose');
  const showingPrev = pane === 'prev';
  /* the sidebar's Compose Email while already here is a new navigation to the
     same route — it doesn't remount the page, so follow it here */
  useEffect(() => {
    setPane(location.state?.pane === 'prev' ? 'prev' : 'compose');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  /* sent history (API) — what actually went out for this class */
  const [sentPage, setSentPage] = useState(1);
  useEffect(() => {
    setSentPage(1);
  }, [networkId]);
  useEffect(() => {
    if (!networkId) return;
    loadSentEmails(networkId, { page: sentPage, limit: SENT_PAGE_SIZE }).catch(() => {});
  }, [networkId, sentPage, loadSentEmails]);

  /* events (API) — the live calendar for this class, so what's offered here is
     what the Calendar page holds right now */
  useEffect(() => {
    if (!networkId) return;
    loadEvents(networkId, { page: 1, limit: 100 }, 'class').catch(() => {});
  }, [networkId, loadEvents]);

  /* ── teacher emails (API, one row per address on this class) ──
     Every "+ Teacher" mode sends to this list, so it's stored on the class
     rather than retyped each time. */
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [teacherSaving, setTeacherSaving] = useState(false);
  const [teacherRemovingId, setTeacherRemovingId] = useState(null);

  const loadTeachers = useCallback(() => {
    if (!networkId) {
      setTeachers([]);
      return;
    }
    setTeachersLoading(true);
    teacherEmailsService
      .list(networkId, { page: 1, limit: 50, status: 'active' })
      .then((r) => setTeachers(r.teachers))
      .catch((err) => {
        setTeachers([]);
        showToast(err.message);
      })
      .finally(() => setTeachersLoading(false));
  }, [networkId]);

  useEffect(loadTeachers, [loadTeachers]);

  /* Save = add the typed address (or a pasted list of them) to the class. The
     API stores one row per address and wants a name on each; we only collect
     the address, so it doubles as the name rather than inventing one. A
     duplicate comes back 409 with a message worth showing as-is. */
  const saveTeacherEmail = async () => {
    if (!networkId) { showToast('Pick a class first.'); return; }
    const entered = parseEmailList(teacherEmailInput);
    if (!entered.length) { showToast("Enter the teacher's email first."); return; }
    const bad = entered.find((e) => !isValidEmail(e));
    if (bad) { showToast(`That doesn't look like an email address: ${bad}`); return; }

    const fresh = entered.filter((e) => !savedTeacherEmails.some((t) => t.toLowerCase() === e.toLowerCase()));
    if (!fresh.length) {
      showToast('Already saved for this class.');
      setTeacherEmailInput('');
      return;
    }

    setTeacherSaving(true);
    const added = [];
    let failure = null;
    for (const email of fresh) {
      try {
        // eslint-disable-next-line no-await-in-loop
        added.push(await teacherEmailsService.add({ networkId, name: email, email }));
      } catch (err) {
        failure = err;
        break;
      }
    }
    if (added.length) {
      setTeachers((prev) => [...prev, ...added].sort((a, b) => a.email.localeCompare(b.email)));
      setTeacherEmailInput('');
    }
    setTeacherSaving(false);
    if (failure) showToast(failure.message);
    else showToast(added.length > 1 ? `${added.length} teacher emails saved ✓` : 'Teacher email saved ✓');
  };

  const removeTeacherEmail = async (teacher) => {
    setTeacherRemovingId(teacher.id);
    try {
      await teacherEmailsService.remove(networkId, teacher.id);
      setTeachers((prev) => prev.filter((t) => t.id !== teacher.id));
      showToast('Teacher removed');
    } catch (err) {
      showToast(err.message);
    } finally {
      setTeacherRemovingId(null);
    }
  };

  /* Every saved address, plus one typed but not saved yet — that one still goes
     out on this send, and the hint says so. */
  const savedTeacherEmails = teachers.map((t) => t.email);
  const unsavedTeacherEmails = uniqueEmails(
    parseEmailList(teacherEmailInput).filter(
      (e) => isValidEmail(e) && !savedTeacherEmails.some((t) => t.toLowerCase() === e.toLowerCase())
    )
  );

  /* sign-ups (API) — the attachable sign-up links for this class */
  useEffect(() => {
    if (!networkId) return;
    loadSignups(networkId, { page: 1, limit: 50 }, 'class').catch(() => {});
  }, [networkId, loadSignups]);

  /* links (API) */
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [excludedLinkIds, setExcludedLinkIds] = useState(new Set());
  const [linksRefresh, setLinksRefresh] = useState(0);
  const [linkFormOpen, setLinkFormOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);

  useEffect(() => {
    if (!networkId) {
      setLinks([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLinksLoading(true);
      try {
        const result = await linksService.list(networkId, { page: 1, limit: 50 });
        /* every link on the class, not just the ones already sent to parents —
           a link is created inactive, and this page is exactly where someone
           goes to send it out for the first time */
        if (!cancelled) setLinks(result.links);
      } catch (err) {
        if (!cancelled) showToast(err.message);
      } finally {
        if (!cancelled) setLinksLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [networkId, linksRefresh]);

  /* "Send to parents" handoff from the Links page: one thing attached, ready to review. */
  const linkDraft = location.state?.linkDraft;
  const draftAppliedRef = useRef(false);
  useEffect(() => {
    if (!linkDraft || draftAppliedRef.current) return;
    draftAppliedRef.current = true;
    setActiveTemplate(null);
    setTplSendAttempted(false);
    setSubject(linkDraft.subject);
    editorRef.current?.setText(linkDraft.body || '');
    setRecipientMode('all_teacher');
    setBuiltin({ consent: !!linkDraft.include.consent, groupchat: !!linkDraft.include.groupchat });
    events.forEach((e) => { if (e.scope === 'class') setIncludeInEmail(e.id, false); });
    signups.forEach((s) => setSignupInclude(s.id, s.id === linkDraft.include.signupId));
    showToast('Draft ready — check the preview, then Send Email ✓');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkDraft]);

  useEffect(() => {
    if (!linkDraft) return;
    setExcludedLinkIds(new Set(links.filter((l) => l.id !== linkDraft.include.linkId).map((l) => l.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links]);

  /* ── template list (API) ── */
  const loadTemplates = useCallback(() => {
    if (!networkId) {
      setTemplates([]);
      return;
    }
    setTemplatesLoading(true);
    emailTemplatesService
      .list(networkId, { page: 1, limit: 20 })
      .then((r) => setTemplates(r.templates))
      .catch((err) => {
        setTemplates([]);
        showToast(err.message);
      })
      .finally(() => setTemplatesLoading(false));
  }, [networkId]);

  useEffect(loadTemplates, [loadTemplates]);

  /* ── template engine ──
     The template is loaded into the editor once, every {{key}} token wrapped in
     a field span (templateBodyToHtml). A field change rewrites the text of its
     own spans and nothing else, so the bold, the link or the wording a room
     parent added around a field survives — rebuilding the draft from the
     master text on every keystroke would throw that away. An unanswered field
     shows its token, which reads red and blocks Send. */
  const tplValue = useCallback(
    (field) => {
      const raw = String(tplValues[field.key] || '').trim();
      if (!raw) return '';
      return field.type === 'date' ? fmtDate(raw) : raw;
    },
    [tplValues]
  );

  const templateFields = activeTemplate?.fields || [];
  /* what each field's spans last showed, so only a changed field is rewritten */
  const shownTplRef = useRef({});

  useEffect(() => {
    if (!activeTemplate) return;
    templateFields.forEach((f) => {
      const shown = tplValue(f) || `{{${f.key}}}`;
      if (shownTplRef.current[f.key] === shown) return;
      shownTplRef.current[f.key] = shown;
      editorRef.current?.setFieldValue(f.key, shown);
    });
    /* carry a teacher address the template collected into the Send To box, one
       Save click away from being kept on the class */
    const teacherField = templateFields.find((f) => /teacher/i.test(f.key) && /email/i.test(f.key));
    const teacherValue = teacherField ? String(tplValues[teacherField.key] || '').trim() : '';
    if (teacherValue) setTeacherEmailInput(teacherValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tplValues, activeTemplate]);

  /* Picking a template: fetch its full definition, then reset the form to it.
     Two conventions keep the approved intro flow working without hard-coding
     it — a template that asks for a teacher's email is addressed to the
     teacher, and one whose copy says HERE links the consent form there. */
  const applyTemplate = async (t) => {
    setTemplateBusyId(t.id);
    try {
      const full = await emailTemplatesService.get(t.templateId || t.id);
      setActiveTemplate(full);
      setTplValues({});
      setMemberLabels({});
      setTplSendAttempted(false);
      if (full.subject) setSubject(full.subject);
      /* every token starts on screen, so nothing needs rewriting until a field is typed */
      shownTplRef.current = Object.fromEntries((full.fields || []).map((f) => [f.key, `{{${f.key}}}`]));
      editorRef.current?.setContent(templateBodyToHtml(full.body || '', full.fields || []), true);
      if (full.fields.some((f) => /teacher/i.test(f.key) && /email/i.test(f.key))) setRecipientMode('teacher');
      setBuiltin((b) => ({ ...b, consent: /\bHERE\b/.test(full.body || '') }));
      setTimeout(() => tplCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
      showToast(`${full.name} loaded — fill in the details up top and the red spots fill themselves ✓`);
    } catch (err) {
      showToast(err.message);
    } finally {
      setTemplateBusyId(null);
    }
  };

  const clearTemplate = () => {
    setActiveTemplate(null);
    setTplValues({});
    setMemberLabels({});
    setTplSendAttempted(false);
    shownTplRef.current = {};
    editorRef.current?.setContent('');
    showToast('Template cleared — back to a plain class email');
  };

  /* First-run helper: publish the client's approved intro copy through the
     templates API so it lives with every other template from then on. */
  const seedIntroTemplate = async () => {
    if (!networkId) { showToast('Pick a class first.'); return; }
    setSeedingTemplate(true);
    try {
      await emailTemplatesService.create({ networkId, ...ROOM_PARENT_INTRO_TEMPLATE });
      showToast('Room Parent Intro template saved ✓');
      loadTemplates();
    } catch (err) {
      showToast(err.message);
    } finally {
      setSeedingTemplate(false);
    }
  };

  /* A `member` field is filled from the class roster. When its key pairs with an
     email field (rp1Name → rp1Email), picking someone fills that too. */
  const pickMember = (field, p) => {
    if (!p) {
      setMemberLabels((prev) => ({ ...prev, [field.key]: 'Someone else — type it below' }));
      return;
    }
    const emailKey = field.key.replace(/Name$/, '') + 'Email';
    const hasEmailField = templateFields.some((f) => f.key === emailKey);
    setTplValues((prev) => ({
      ...prev,
      [field.key]: p.name,
      ...(hasEmailField ? { [emailKey]: p.email || '' } : {}),
    }));
    setMemberLabels((prev) => ({ ...prev, [field.key]: p.name }));
  };

  const missingTplFields = templateFields.filter((f) => f.isRequired && !tplValue(f));
  const leftoverPlaceholders = PH_RE.test(draft.text);
  /* a file still uploading, or one that failed, holds Send too (spec/32) */
  const attachProblem = attachIssue(att.attachments);
  const sendBlocked = missingTplFields.length > 0 || (templateActive && leftoverPlaceholders) || !!attachProblem;
  /* the editor paints [brackets] red itself; this only decides whether to say so */
  const hasPh = PH_RE.test(draft.text);

  /* ── preview (live) ── */
  const classEvents = [...events.filter((e) => e.scope === 'class')].sort((a, b) => a.date.localeCompare(b.date));
  const includedEvents = classEvents.filter((e) => e.includeInEmail !== false);
  const includedLinks = links.filter((l) => !excludedLinkIds.has(l.id));
  const scopedSignups = signups.filter((s) => s.scope === 'class');
  const includedSignups = scopedSignups.filter((s) => s.includeInEmail !== false);

  /* One anchor, or plain text when the thing has no link yet (a sign-up saved
     before short links existed, an event whose share URL never came back). */
  const anchor = (rawHref, text) => {
    const href = safeUrl(rawHref);
    return href
      ? `<a href="${escapeAttr(href)}" target="_blank" rel="noreferrer">${escapeHtml(text)}</a>`
      : `<b>${escapeHtml(text)}</b>`;
  };

  /* The message itself — identical for the on-screen preview and the email that
     goes out, except that the preview highlights [placeholders] in red. Three
     decorations are applied at render, on text nodes and never inside a link
     the room parent made: the first HERE becomes the consent-form link,
     placeholders turn red (preview only), and a date a template asked for
     reads bold as a deadline. None of them is stored in the draft. */
  const buildBodyHtml = ({ forEmail = false } = {}) => {
    const deadlines = templateFields.filter((f) => f.type === 'date').map((f) => tplValue(f)).filter(Boolean);
    let introHtml = decorateHtml(draft.html, { consentUrl, deadlines, markPlaceholders: !forEmail });
    /* mail clients ignore <style>, so paragraph spacing rides on the elements */
    if (forEmail) introHtml = emailBodyStyles(introHtml);

    let body = introHtml;
    if (includedEvents.length) {
      body += '<b>Upcoming events</b><br>';
      includedEvents.forEach((ev) => {
        const timeStr = ev.startTime ? ` (${fmtEventTime(ev.startTime, ev.endTime)})` : '';
        body += `• ${anchor(ev.publicURL, ev.title)} — ${fmtDate(ev.date)}${timeStr}<br>`;
        if (ev.desc) body += `&nbsp;&nbsp;&nbsp;${escapeHtml(ev.desc)}<br>`;
      });
      body += '<br>';
    }
    if (builtin.consent || builtin.groupchat || includedLinks.length || includedSignups.length) {
      body += '<b>Helpful links</b><br>';
      if (builtin.consent) body += `• ${anchor(consentUrl, 'Parent Consent Form')}<br>`;
      if (builtin.groupchat) body += `• ${anchor(chatUrl, `${ctx.name} Group Chat`)}<br>`;
      includedLinks.forEach((l) => { body += `• ${anchor(l.url, l.label)}<br>`; });
      includedSignups.forEach((s) => { body += `• ${anchor(signupLinkUrl(s), `${s.title} (Sign-Up)`)}<br>`; });
      body += '<br>';
    }
    const hasOwnSignOff = /^—/m.test(draft.text);
    if (!hasOwnSignOff) {
      const signer = myFirstName ? `${escapeHtml(myFirstName)} — ` : '';
      body += `Thanks!<br>${signer}Room Parent, ${escapeHtml(ctx.name)}`;
    }
    return body;
  };

  const buildPreviewHtml = () =>
    `<div class="subj">${escapeHtml(subject.trim() || 'Class Update')}</div>` + buildBodyHtml();

  /* A standalone document for the mail client — styles have to be inline, none
     of the app's CSS travels with it. No background colour on purpose: the
     client wants the email to sit on the inbox's own background, and the text
     starts at the left like an ordinary message rather than a centred column. */
  const buildEmailHtml = () =>
    `<!DOCTYPE html><html><head><meta charset="utf-8"></head>` +
    `<body style="margin:0;padding:0;">` +
    `<div style="max-width:600px;margin:0;padding:24px 20px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#26302a;">` +
    buildBodyHtml({ forEmail: true }) +
    `</div></body></html>`;

  const sendToChip =
    recipientMode === 'all_teacher' ? 'All Families + Teacher'
      : recipientMode === 'all' ? 'All Families'
        : recipientMode === 'emailonly_teacher' ? 'Email-Only Families + Teacher'
          : recipientMode === 'teacher' ? 'To the Teacher'
            : `${selectedIds.size} selected`;

  const [sending, setSending] = useState(false);

  const sendEmail = async () => {
    /* template fields are a hard requirement — the email can't send until every
       one is filled and no red placeholder remains */
    setTplSendAttempted(true);
    if (sendBlocked) {
      if (attachProblem && !missingTplFields.length && !(templateActive && leftoverPlaceholders)) {
        window.alert(attachProblem.long);
        return;
      }
      tplCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.alert(
        missingTplFields.length
          ? 'Fill these in first:\n\n• ' + missingTplFields.map((f) => f.label).join('\n• ')
          : 'The draft still has [bracketed] placeholders in it. Fill them in before sending.'
      );
      return;
    }
    const finalSubject = subject.trim() || 'Class Update';
    /* the saved list, plus anything typed but not saved yet — nobody should
       lose a send because they skipped the Save button */
    const activeTeacherEmails = uniqueEmails([...savedTeacherEmails, ...unsavedTeacherEmails]);
    const teacherRecipients = activeTeacherEmails.map((e) => ({ name: e, email: e }));
    let recipients;
    let summary;
    let familyCount = 0;
    let teacherAdded = false;
    if (recipientMode === 'all_teacher') {
      familyCount = members.length;
      teacherAdded = teacherRecipients.length > 0;
      recipients = [...members, ...teacherRecipients];
      summary = 'All Families + Teacher';
    } else if (recipientMode === 'all') {
      recipients = members;
      familyCount = members.length;
      summary = 'All Families';
    } else if (recipientMode === 'emailonly_teacher') {
      const emailOnly = members.filter((p) => !p.onSprout);
      familyCount = emailOnly.length;
      teacherAdded = teacherRecipients.length > 0;
      recipients = [...emailOnly, ...teacherRecipients];
      summary = 'Email-Only Families + Teacher';
    } else if (recipientMode === 'teacher') {
      if (!teacherRecipients.length) { window.alert("Add the teacher's email first."); return; }
      recipients = teacherRecipients;
      summary = activeTeacherEmails.join(', ');
    } else {
      recipients = members.filter((p) => selectedIds.has(p.id));
      familyCount = recipients.length;
      summary = 'Selected People';
    }
    if (!recipients.length) { window.alert('Select at least one recipient.'); return; }

    /* Only people with an address on file can actually be emailed — say so
       rather than reporting a send that reached fewer people than claimed. */
    const reachable = recipients.filter((r) => r.email && isValidEmail(r.email));
    const addresses = uniqueEmails(reachable.map((r) => r.email));
    const noEmail = recipients.length - reachable.length;
    if (!addresses.length) {
      window.alert('None of the selected recipients have an email address on file.');
      return;
    }
    if (!gmailAccount) {
      showToast('Connect Gmail at the top right first — that\'s the account the email sends from.');
      return;
    }

    const emailHtml = buildEmailHtml();
    /* the plain-text twin — some mail apps show only this part */
    const emailText = htmlToText(buildBodyHtml({ forEmail: true }));
    const files = att.attachments.filter((a) => a.state === 'ready');
    setSending(true);
    /* the bytes are still here in the browser: Gmail gets the file itself, the
       record keeps where the upload landed */
    let mimeFiles;
    try {
      mimeFiles = await Promise.all(
        files.map(async (a) => ({
          filename: a.name,
          contentType: a.contentType || a.file.type || 'application/octet-stream',
          base64: await fileToBase64(a.file),
        }))
      );
    } catch {
      showToast('Could not read an attachment from disk — remove it, attach it again and retry.');
      setSending(false);
      return;
    }
    try {
      /* everyone on Bcc: one message, and no parent sees another's address */
      await sendGmailMessage({
        to: [gmailAccount.email],
        bcc: addresses,
        subject: finalSubject,
        html: emailHtml,
        text: emailText,
        attachments: mimeFiles,
        fromName: gmailAccount.name,
        fromEmail: gmailAccount.email,
      });
    } catch (err) {
      showToast(err.message);
      setSending(false);
      return;
    }

    /* The mail is gone; record what went out. A failed record must not read as
       a failed send — say plainly that only the history entry didn't save. */
    try {
      const record = await sentEmailsService.create({
        networkId,
        subject: finalSubject,
        body: emailHtml,
        to: [gmailAccount.email],
        bcc: addresses,
        templateId: activeTemplate?.templateId || '',
        templateName: activeTemplate?.name || '',
        fieldValues: templateActive ? tplValues : {},
        attachments: files.map(({ name, size, kind, label, url, key, contentType }) => ({ name, size, kind, label, url, key, contentType })),
      });
      prependSentEmail(record);
    } catch (err) {
      showToast(`Email sent ✓ — but saving it to your sent history failed: ${err.message}`);
    }
    setSending(false);

    /* The draft went with the email. Left in place, the files — and the message
       — would ride along on the next one, so the draft resets; who it goes to
       and which events and links are ticked stay as they were. */
    const sentFileCount = files.length;
    setSubject('');
    editorRef.current?.setContent('');
    att.clear();
    setActiveTemplate(null);
    setTplValues({});
    setMemberLabels({});
    setTplSendAttempted(false);
    shownTplRef.current = {};

    /* Including an event in an email that actually reaches parents is the same as
       inviting them to it — mark those Invitation Sent. Not for teacher-only sends. */
    if (recipientMode !== 'teacher') {
      const ids = classEvents.filter((e) => e.includeInEmail !== false && !e.sent).map((e) => e.id);
      if (ids.length) markEventsSent(ids);

      /* Same for links and sign-ups: one that just went out to families has
         been sent to parents, so publish the ones still sitting as drafts and
         the Links page agrees with what happened. Best-effort — the email is
         already gone, a failed status patch is not worth an error. */
      const draftLinks = includedLinks.filter((l) => !l.isActive);
      draftLinks.forEach((l) => {
        linksService
          .setStatus(l.id, true)
          .then(() => setLinks((prev) => prev.map((x) => (x.id === l.id ? { ...x, isActive: true } : x))))
          .catch(() => {});
      });
      includedSignups
        .filter((s) => !s.status)
        .forEach((s) => {
          signupsService
            .setStatus(s.id, true)
            .then(() => patchSignup(s.id, { status: true }))
            .catch(() => {});
        });
    }

    const withFiles = sentFileCount ? `, with ${sentFileCount} attachment${sentFileCount === 1 ? '' : 's'}` : '';
    if (noEmail > 0) {
      showToast(`Sent from ${gmailAccount.email} to ${addresses.length}${withFiles} ✓ — ${noEmail} had no email address on file`);
      return;
    }
    if (recipientMode === 'teacher') {
      showToast(`Sent to ${summary}${withFiles} ✓`);
    } else if (recipientMode === 'all_teacher' || recipientMode === 'emailonly_teacher') {
      showToast(
        teacherAdded
          ? `Sent to ${familyCount} famil${familyCount === 1 ? 'y' : 'ies'} + ${activeTeacherEmails.join(', ')}${withFiles} ✓`
          : `Sent to ${familyCount} famil${familyCount === 1 ? 'y' : 'ies'}${withFiles} ✓ — no teacher email saved for this class, so they weren't included`
      );
    } else {
      showToast(`Sent to ${recipients.length} famil${recipients.length === 1 ? 'y' : 'ies'} (${summary})${withFiles} ✓`);
    }
  };

  const rpOptions = members.filter((m) => m.isRoomParent).length ? members.filter((m) => m.isRoomParent) : members;
  const allMembersSelected = members.length > 0 && members.every((p) => selectedIds.has(p.id));
  /* the API already returns newest first */
  const missingHint = missingTplFields.length
    ? `Still needed: ${missingTplFields.map((f) => f.label).join(', ')}.`
    : templateActive && leftoverPlaceholders
      ? 'Fill in every red placeholder in the draft before sending.'
      : attachProblem
        ? attachProblem.long
        : '';
  /* the files as the inbox will list them — a failed one is left out */
  const previewFiles = att.attachments.filter((a) => a.state !== 'failed');
  /* the phone's Subject-and-message block says the files are there while closed */
  const fileCount = att.attachments.length;
  const writeFlag = !fileCount
    ? null
    : attachProblem
      ? attachProblem.short === 'Attachment failed' ? 'Failed' : 'Uploading'
      : `${fileCount} file${fileCount === 1 ? '' : 's'}`;

  /* ── the phone layout ── The desktop screen is one long column of 25 inputs;
     on a phone the same sections collapse into blocks you open one at a time,
     and the preview moves into a sheet reached from a fixed dock. */
  const isPhone = useMediaQuery('(max-width: 760px)');
  const [openBlock, setOpenBlock] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const toggleBlock = (key) => setOpenBlock((cur) => (cur === key ? null : key));

  /* the sheet belongs to the phone layout — leaving it open across a resize
     would strand it over the desktop grid */
  useEffect(() => {
    if (!isPhone) setSheetOpen(false);
  }, [isPhone]);

  const selectedLinkLabels = [
    ...(builtin.consent ? ['Parent Consent Form'] : []),
    ...(builtin.groupchat ? [`${ctx.name} Group Chat`] : []),
    ...includedLinks.map((l) => l.label),
    ...includedSignups.map((s) => `${s.title} (Sign-Up)`),
  ];

  const tplSummary = templateActive
    ? missingTplFields.length
      ? `${missingTplFields.length} of ${templateFields.filter((f) => f.isRequired).length} still needed`
      : `${activeTemplate?.name || 'Template'} · all filled in`
    : 'Optional · you are writing from scratch';

  /* the dock says where the draft stands: nothing written yet, blocked on a
     blank, or good to go */
  const nothingWritten = !subject.trim() && !draft.text.trim();
  const dockBlocked = nothingWritten || sendBlocked;
  const dockMain = nothingWritten
    ? 'Write your email'
    : missingTplFields.length || (templateActive && leftoverPlaceholders)
      ? `${missingTplFields.length || ''} blank${missingTplFields.length === 1 ? '' : 's'} left`.trim()
      : attachProblem
        ? attachProblem.short
        : 'Ready to send';

  /* Every section below is built once, as a value, then placed by whichever
     layout is on screen: the desktop two-column grid, or the phone accordion
     with its preview sheet. Building them rather than duplicating them keeps one
     of every input, so there is no second copy of the draft to keep in sync. */
  const quickActionsCard = (
    <Card
      title="Quick Actions"
      action={
        templateActive ? (
          <button className="btn btn-ghost btn-sm" onClick={clearTemplate}>Clear template</button>
        ) : null
      }
    >
      {templatesLoading && (
        <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0' }}>
          <Spinner variant="green" /> Loading templates…
        </div>
      )}
      {!templatesLoading && templates.length === 0 && (
        <>
          <EmptyState>No email templates for this class yet.</EmptyState>
          <div className="inline-form-actions" style={{ borderTop: 'none', justifyContent: 'flex-start' }}>
            <button className="btn btn-primary btn-sm" disabled={seedingTemplate} onClick={seedIntroTemplate}>
              {seedingTemplate ? <><Spinner /> Saving…</> : '+ Add the Room Parent Intro template'}
            </button>
          </div>
        </>
      )}
      {!templatesLoading && templates.length > 0 && (
        <div className="quick-actions">
          {templates.map((t) => (
            <button
              className="quick-action"
              key={t.id}
              disabled={templateBusyId === t.id}
              onClick={() => applyTemplate(t)}
            >
              <img src="/assets/icons/mail.svg" alt="" />
              <span className="qa-copy">
                <b>
                  {t.name}
                  {activeTemplate?.templateId === t.templateId ? (
                    <span className="src-chip" style={{ marginLeft: 6 }}>In use</span>
                  ) : null}
                </b>
                <small>{t.description || 'Fill in the details and send'}</small>
              </span>
              <span className="arrow">{templateBusyId === t.id ? '…' : '›'}</span>
            </button>
          ))}
        </div>
      )}
    </Card>
  );

  const tplFieldsCard = templateActive ? (
    <div ref={tplCardRef}>
      <Card
        className="req-card"
        title="Fill in your template details"
        action={
          <span
            className="req-flag"
            style={
              missingTplFields.length
                ? undefined
                : { background: 'var(--green-light)', color: 'var(--green)' }
            }
          >
            {missingTplFields.length ? `${missingTplFields.length} still needed` : 'All filled ✓'}
          </span>
        }
      >
        <div className="hint" style={{ marginBottom: 12 }}>
          Type these once — every red placeholder in the draft below updates as you go.
        </div>
        {/* every row here comes from the template's own field definitions,
            so a new template means new rows and no code change */}
        <div className="form-grid">
          {templateFields.map((f) => {
            const value = tplValues[f.key] || '';
            const setValue = (v) => setTplValues((prev) => ({ ...prev, [f.key]: v }));
            const missing = tplSendAttempted && f.isRequired && !tplValue(f);
            const fullWidth = f.type === 'textarea' || f.type === 'date' || f.type === 'member';
            return (
              <Field
                key={f.key}
                label={f.label}
                hint={f.helpText || undefined}
                className={missing ? 'missing' : ''}
                style={fullWidth ? { gridColumn: '1/-1' } : undefined}
              >
                {f.type === 'member' && (
                  <div style={{ display: 'grid', gap: 8 }}>
                    <MemberPicker
                      label={memberLabels[f.key] || '— Select from the class —'}
                      options={rpOptions}
                      onPick={(p) => pickMember(f, p)}
                    />
                    <input
                      type="text"
                      value={value}
                      placeholder={f.placeholder || 'Or type a name'}
                      onChange={(e) => setValue(e.target.value)}
                    />
                  </div>
                )}
                {f.type === 'textarea' && (
                  <textarea rows={3} value={value} placeholder={f.placeholder} onChange={(e) => setValue(e.target.value)} />
                )}
                {f.type === 'select' && (
                  <select value={value} onChange={(e) => setValue(e.target.value)}>
                    <option value="">— Select —</option>
                    {f.options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                )}
                {f.type === 'date' && (
                  <div className="date-input-wrap">
                    <svg className="cal-ic" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
                    <input type="date" value={value} onChange={(e) => setValue(e.target.value)} />
                  </div>
                )}
                {(f.type === 'text' || f.type === 'email') && (
                  <input type="text" value={value} placeholder={f.placeholder} onChange={(e) => setValue(e.target.value)} />
                )}
              </Field>
            );
          })}
        </div>
      </Card>
    </div>
  ) : null;

  const sendToCard = (
    <Card>
      <Field label="Send To">
        <div className="radio-row">
          {[
            ['all_teacher', 'All Families + Teacher', null],
            ['all', 'All Families', ' — no teacher, e.g. teacher appreciation'],
            ['specific', 'Select Specific People', null],
            ['emailonly_teacher', 'Email-Only Families + Teacher', null],
            ['teacher', 'Send to Teacher', null],
          ].map(([value, label, note]) => (
            <label key={value}>
              <input
                type="radio"
                name="emailRecipient"
                value={value}
                checked={recipientMode === value}
                onChange={() => setRecipientMode(value)}
              />
              {label}
              {note && <span style={{ color: 'var(--muted)', fontWeight: 400 }}>{note}</span>}
            </label>
          ))}
        </div>
        {recipientMode === 'specific' && (
          <div style={{ marginTop: 10 }}>
            {membersLoading && (
              <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0' }}>
                <Spinner variant="green" /> Loading parents…
              </div>
            )}
            {!membersLoading && members.length === 0 && (
              <EmptyState>No parents on this class yet — add them on the Parents page.</EmptyState>
            )}
            {!membersLoading && members.length > 0 && (
              <label className="chk-row chk-row-all">
                <input
                  type="checkbox"
                  checked={allMembersSelected}
                  onChange={(e) => setSelectedIds(e.target.checked ? new Set(members.map((p) => p.id)) : new Set())}
                />
                <div className="body">
                  <div className="t">Select All ({members.length})</div>
                  <div className="d">
                    {members.filter((p) => p.email).length} with an email address on file
                  </div>
                </div>
              </label>
            )}
            {!membersLoading &&
              members.map((p) => (
                <label className="chk-row" key={p.id}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(p.id)}
                    onChange={(e) =>
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(p.id);
                        else next.delete(p.id);
                        return next;
                      })
                    }
                  />
                  <div className="body">
                    <div className="t">
                      {p.name}
                      {p.kidsName ? <span style={{ color: 'var(--muted)', fontWeight: 400 }}> · {p.kidsName}</span> : null}
                    </div>
                    <div className="d">
                      {p.onSprout ? 'On the app' : 'Email only'}
                      {p.email ? ' · ' + p.email : ' · no email on file'}
                    </div>
                  </div>
                </label>
              ))}
          </div>
        )}
        {TEACHER_MODES.includes(recipientMode) && (
          <Field
            label={teachers.length > 1 ? "Teachers' Emails" : "Teacher's Email"}
            style={{ marginTop: 10, marginBottom: 0 }}
            hint={
              unsavedTeacherEmails.length
                ? 'This send will include it — hit Save to keep it for next time.'
                : 'Saved on this class, so every "+ Teacher" send uses it. Add as many teachers as you need.'
            }
          >
            {teachersLoading && (
              <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0 8px' }}>
                <Spinner variant="green" /> Loading teachers…
              </div>
            )}
            {teachers.length > 0 && (
              <div className="tag-select-row" style={{ marginBottom: 8 }}>
                {teachers.map((t) => (
                  <span className="diet-chip" key={t.id} style={{ cursor: 'default' }}>
                    {t.email}
                    <span
                      title="Remove"
                      style={{ cursor: 'pointer', color: 'var(--red)', fontWeight: 700 }}
                      onClick={() => { if (teacherRemovingId !== t.id) removeTeacherEmail(t); }}
                    >
                      ×
                    </span>
                  </span>
                ))}
              </div>
            )}
            <div className="link-add-row">
              <input
                type="text"
                value={teacherEmailInput}
                placeholder="Enter teacher's email"
                onChange={(e) => setTeacherEmailInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveTeacherEmail(); } }}
              />
              <button className="btn btn-primary" disabled={teacherSaving} onClick={saveTeacherEmail}>
                {teacherSaving ? <><Spinner /> Saving…</> : 'Save'}
              </button>
            </div>
          </Field>
        )}
      </Field>
    </Card>
  );

  const draftCard = (
    <Card title="Draft Email">
      <Field label="Subject" style={{ marginBottom: 14 }}>
        <input
          type="text"
          value={subject}
          placeholder="What's this email about?"
          onChange={(e) => setSubject(e.target.value)}
        />
      </Field>
      <Field label="Message">
        <RichEditor ref={editorRef} placeholder="Write your message to parents" ariaLabel="Message" onChange={setDraft} />
        {hasPh && (
          <div className="hint">
            Anything still in <b style={{ color: 'var(--red)' }}>red brackets</b> has to be filled in before this can send.
          </div>
        )}
      </Field>
      {/* under the message, where Gmail puts it (spec/32) — one field, so on a
          phone it travels with the Subject and message block */}
      <EmailAttachments
        attachments={att.attachments}
        rejects={att.rejects}
        onAdd={att.add}
        onRetry={att.retry}
        onRemove={att.remove}
        onDismissRejects={att.dismissRejects}
      />
    </Card>
  );

  const eventsCard = (
    <Card title="Events to Include">
      <div>
        {eventsLoading && (
          <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0' }}>
            <Spinner variant="green" /> Loading events…
          </div>
        )}
        {!eventsLoading && classEvents.length === 0 && <EmptyState>No upcoming events.</EmptyState>}
        {!eventsLoading &&
          classEvents.map((ev) => (
            <label className="chk-row" key={ev.id}>
              <input
                type="checkbox"
                checked={ev.includeInEmail !== false}
                onChange={(e) => setIncludeInEmail(ev.id, e.target.checked)}
              />
              <div className="body">
                <div className="t">
                  {ev.title} — {fmtDate(ev.date)}
                  {ev.startTime ? ` (${fmtEventTime(ev.startTime, ev.endTime)})` : ''}
                </div>
                {/* the share link that goes into the email for this event */}
                <div className="d">{ev.publicURL || ev.desc}</div>
              </div>
            </label>
          ))}
      </div>
    </Card>
  );

  const linksCard = (
    <Card
      title="Links to Include"
      action={
        <button
          className="btn btn-primary btn-sm"
          onClick={() => (linkFormOpen && !editingLink ? (setLinkFormOpen(false), setEditingLink(null)) : (setEditingLink(null), setLinkFormOpen(true)))}
        >
          + Add Link
        </button>
      }
    >
      <LinkForm
        open={linkFormOpen}
        editingLink={editingLink}
        networkId={networkId}
        onClose={() => {
          setLinkFormOpen(false);
          setEditingLink(null);
        }}
        onSaved={() => setLinksRefresh((k) => k + 1)}
      />
      {linksLoading && (
        <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0' }}>
          <Spinner variant="green" /> Loading links…
        </div>
      )}
      <div>
        {/* the consent form and the class group chat always exist for a class */}
        <label className="chk-row">
          <input type="checkbox" checked={builtin.consent} onChange={(e) => setBuiltin({ ...builtin, consent: e.target.checked })} />
          <div className="body">
            <div className="t">
              Parent Consent Form <span className="src-chip">Always available</span>
            </div>
            <div className="d">{consentUrl}</div>
          </div>
        </label>
        <label className="chk-row">
          <input type="checkbox" checked={builtin.groupchat} onChange={(e) => setBuiltin({ ...builtin, groupchat: e.target.checked })} />
          <div className="body">
            <div className="t">
              {ctx.name} Group Chat <span className="src-chip">Always available</span>
            </div>
            <div className="d">{chatUrl}</div>
          </div>
        </label>
        {links.map((l) => (
          <label className="chk-row" key={l.id}>
            <input
              type="checkbox"
              checked={!excludedLinkIds.has(l.id)}
              onChange={(e) =>
                setExcludedLinkIds((prev) => {
                  const next = new Set(prev);
                  if (e.target.checked) next.delete(l.id);
                  else next.add(l.id);
                  return next;
                })
              }
            />
            <div className="body">
              <div className="t">
                {l.label}
                {!l.isActive && (
                  <span className="ev-status draft" style={{ marginLeft: 6 }}>Not sent yet</span>
                )}
              </div>
              <div className="d">{l.url}</div>
            </div>
          </label>
        ))}
        {scopedSignups.map((s) => (
          <label className="chk-row" key={`su-${s.id}`}>
            <input
              type="checkbox"
              checked={s.includeInEmail !== false}
              onChange={(e) => setSignupInclude(s.id, e.target.checked)}
            />
            <div className="body">
              <div className="t">{s.title} (Sign-Up)</div>
              <div className="d">{signupLinkUrl(s)}</div>
            </div>
          </label>
        ))}
      </div>
    </Card>
  );

  /* Previous Emails is its own tab rather than a card under Compose — the
     client didn't want the history hidden below the links. The header swaps
     with the tab, as the design does. */
  const sectionHead = showingPrev ? (
    <SectionHead title="Previous Emails" sub="Every email you've sent for this class, newest first." />
  ) : (
    <SectionHead
      title="Compose Class Email"
      sub="Pulls upcoming calendar events and your class links in automatically — uncheck anything you don't want included. Send from the preview when it looks right."
    />
  );

  const emailTabs = (
    <div className="email-tabs" role="tablist" aria-label="Email">
      <button
        type="button"
        role="tab"
        className={showingPrev ? '' : 'on'}
        aria-selected={!showingPrev}
        onClick={() => setPane('compose')}
      >
        Compose Email
      </button>
      <button
        type="button"
        role="tab"
        className={showingPrev ? 'on' : ''}
        aria-selected={showingPrev}
        onClick={() => setPane('prev')}
      >
        Previous Emails {sentEmailsTotal > 0 && <span className="n">{sentEmailsTotal}</span>}
      </button>
    </div>
  );

  const prevSentCard = (
    <Card title="Previous Emails">
      <div>
        {sentEmailsLoading && (
          <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0' }}>
            <Spinner variant="green" /> Loading sent emails…
          </div>
        )}
        {!sentEmailsLoading && sentEmails.length === 0 && <EmptyState>No emails sent yet.</EmptyState>}
        {!sentEmailsLoading &&
          sentEmails.map((e) => (
            <div className="ev-row-mini" style={{ cursor: 'default' }} key={e.id}>
              <img src="/assets/icons/mail.svg" alt="" style={{ width: 38, height: 38, borderRadius: 12 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="ev-title">
                  {e.subject}
                  {e.templateName ? <span className="src-chip" style={{ marginLeft: 6 }}>{e.templateName}</span> : null}
                  {e.status && e.status !== 'sent' ? (
                    <span className="ev-status draft" style={{ marginLeft: 6 }}>{e.status}</span>
                  ) : null}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                  {e.count} recipient{e.count === 1 ? '' : 's'}
                  {e.sentDate ? ` · ${fmtDateTime(e.sentDate)}` : ''}
                  {e.attachments.length > 0 && (
                    <>
                      {' · '}
                      <span className="sent-clip" title={`${e.attachments.length} attachment${e.attachments.length === 1 ? '' : 's'}`}>
                        <IconPaperclip />
                        {e.attachments.length}
                      </span>
                    </>
                  )}
                </div>
                {/* the files that went with it, each opening its stored copy */}
                {e.attachments.length > 0 && <AttachmentChips files={e.attachments} style={{ marginTop: 8 }} />}
              </div>
            </div>
          ))}
        {sentEmailsTotalPages > 1 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              marginTop: 12,
              paddingTop: 12,
              borderTop: '1px solid var(--border)',
            }}
          >
            <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>
              {sentEmailsTotal} email{sentEmailsTotal === 1 ? '' : 's'} · Page {sentPage} of {sentEmailsTotalPages}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="btn btn-ghost btn-sm"
                disabled={sentPage <= 1 || sentEmailsLoading}
                onClick={() => setSentPage((p) => p - 1)}
              >
                ← Prev
              </button>
              <button
                className="btn btn-ghost btn-sm"
                disabled={sentPage >= sentEmailsTotalPages || sentEmailsLoading}
                onClick={() => setSentPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );

  const sendButton = (
    <button
      className="btn btn-primary btn-sm"
      disabled={sendBlocked || sending}
      title={gmailAccount ? `Sends from ${gmailAccount.email}` : 'Connect Gmail first'}
      onClick={sendEmail}
    >
      {sending ? (
        <><Spinner /> Sending…</>
      ) : (
        <><img className="btn-icon-img" src="/assets/icons/mail.svg" alt="" />Send Email</>
      )}
    </button>
  );

  const renderPreviewCard = (withSend) => (
    <Card
      style={withSend ? { position: 'sticky', top: 0 } : undefined}
      title="Preview"
      action={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="src-chip">{sendToChip}</span>
          {withSend && sendButton}
        </div>
      }
    >
      <div className="email-preview rte-out">
        <div dangerouslySetInnerHTML={{ __html: buildPreviewHtml() }} />
        {/* the files, after the sign-off, the way the inbox lists them */}
        {previewFiles.length > 0 && (
          <div className="pv-attach">
            <div className="pv-h">{previewFiles.length} attachment{previewFiles.length === 1 ? '' : 's'}</div>
            <AttachmentChips files={previewFiles} />
          </div>
        )}
      </div>
      {sendBlocked && missingHint && (
        <div className="hint" style={{ marginTop: 8, color: 'var(--red)' }}>{missingHint}</div>
      )}
      <div className="hint" style={{ marginTop: 8 }}>
        {gmailAccount
          ? `Sends from ${gmailAccount.email} — everyone is Bcc'd, and a copy lands in that account's Sent folder.`
          : 'Connect Gmail at the top right to send — the email goes out from your own Gmail account.'}
      </div>
    </Card>
  );

  if (isPhone) {
    return (
      /* the bottom padding makes room for the dock, which only Compose has */
      <div className={`sec on${showingPrev ? '' : ' compose-phone'}`}>
        {/* the Gmail account this sends from is connected in the Topbar */}
        {sectionHead}
        {emailTabs}

        {showingPrev && prevSentCard}

        {/* hidden, not unmounted, so a half-written draft survives a look at
            Previous Emails */}
        <div style={showingPrev ? { display: 'none' } : undefined}>
        <div className="email-blocks">
          <ComposeBlock
            icon={<IconFile />}
            title={templateActive ? 'Template details' : 'Start from a template'}
            summary={tplSummary}
            flag={templateActive ? (missingTplFields.length || 'Done') : null}
            flagTone={missingTplFields.length ? 'warn' : 'ok'}
            open={openBlock === 'tpl'}
            onToggle={() => toggleBlock('tpl')}
          >
            {quickActionsCard}
            {tplFieldsCard}
          </ComposeBlock>

          <ComposeBlock
            icon={<IconEdit />}
            title="Subject and message"
            summary={subject.trim() || 'No subject yet'}
            flag={writeFlag}
            flagTone={attachProblem ? 'warn' : 'ok'}
            open={openBlock === 'write'}
            onToggle={() => toggleBlock('write')}
          >
            {draftCard}
          </ComposeBlock>

          <ComposeBlock
            icon={<IconCal />}
            title="Events to include"
            summary={summarise(includedEvents.map((e) => e.title))}
            flag={includedEvents.length}
            open={openBlock === 'events'}
            onToggle={() => toggleBlock('events')}
          >
            {eventsCard}
          </ComposeBlock>

          <ComposeBlock
            icon={<IconLink />}
            title="Links to include"
            summary={summarise(selectedLinkLabels)}
            flag={selectedLinkLabels.length}
            open={openBlock === 'links'}
            onToggle={() => toggleBlock('links')}
          >
            {linksCard}
          </ComposeBlock>
        </div>

        {/* the dock reports where the draft stands and is the only way into the
            preview on a phone — the preview card itself lives in the sheet */}
        <div className="email-dock">
          <div className="sum">
            <b className={dockBlocked ? 'warn' : ''}>{dockMain}</b>
            {includedEvents.length} event{includedEvents.length === 1 ? '' : 's'} ·{' '}
            {selectedLinkLabels.length} link{selectedLinkLabels.length === 1 ? '' : 's'}
            {fileCount ? ` · ${fileCount} file${fileCount === 1 ? '' : 's'}` : ''} · {sendToChip}
          </div>
          <button className="btn btn-primary" onClick={() => setSheetOpen(true)}>Preview</button>
        </div>

        <PreviewSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setSheetOpen(false)}>Keep editing</button>
              {sendButton}
            </>
          }
        >
          {renderPreviewCard(false)}
          {/* recipients come after the preview: you check what it says, then who
              it goes to */}
          {sendToCard}
        </PreviewSheet>
        </div>
      </div>
    );
  }

  return (
    <div className="sec on">
      {/* the Gmail account this sends from is connected in the Topbar */}
      {sectionHead}
      {emailTabs}

      {showingPrev && prevSentCard}

      {/* hidden, not unmounted, so a half-written draft survives a look at
          Previous Emails */}
      <div style={showingPrev ? { display: 'none' } : undefined}>
        {quickActionsCard}

        {tplFieldsCard}

        {sendToCard}

        <div className="email-grid">
          <div>
            {draftCard}
            {eventsCard}
            {linksCard}
          </div>
          {renderPreviewCard(true)}
        </div>
      </div>
    </div>
  );
}
